// Return Status: a refund overdue on a return delivered to us (2026-10-29,
// Jenn; docs/decisions/2026-10-29-return-refund-overdue.md,
// docs/pipeline-behaviors.md "Return Status: a refund overdue").
//
// The warehouse refunds a return once it processes it (the RTN). The label
// email promises "Up to 3-5 business days once parts have been delivered".
// Within that window the stage "Delivered to us" keeps its "Refund Delivered"
// reply. Once the return has been delivered MORE than the window before today
// with no refund on the order, the stage shows a refund step (the scrap
// refund), an email to Support asking them to zero out the RMA, and the order
// note, and its reply becomes "Refund Confirmation".
//
// How the panel and the server agree: the overdue steps carry their own path,
// RETURN_STAGE_DELIVERED_OVERDUE, and the stage choice step carries the
// window (params.refundOverdue.windowBusinessDays). returnStagePathKey() turns
// the choice and its date into the path that is on: "delivered" within the
// window, "delivered_overdue" past it. Everything that compares a step's
// taskPath with the choice (the panel, isTaskPathBlocked(), the promise
// check's withoutOffPathSteps()) compares it with that key, so changing the
// stage or its date re-decides whether the refund step shows.
//
// Pure, on calendar dates (YYYY-MM-DD), business days from business-days.js.
import { addBusinessDays, isBusinessDay } from './business-days.js'

// The window (Jenn, 2026-10-29). The server's setting
// (config.returnRefundOverdueBusinessDays) defaults to it; the stage step
// carries the value used.
export const RETURN_REFUND_OVERDUE_BUSINESS_DAYS = 5
export const RETURN_STAGE_DELIVERED_OVERDUE = 'delivered_overdue'
export const RETURN_STAGE_KIND = 'return_stage'

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
function isDate(v) {
  const m = DATE_RE.exec(String(v ?? ''))
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}
// Today in America/New_York (the same day tasks.js's easternDate() gives).
export function overdueToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now)
  const get = (t) => parts.find((p) => p.type === t)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}
export function mdyDate(iso) {
  if (!isDate(iso)) return null
  const [y, m, d] = String(iso).split('-')
  return `${m}/${d}/${y}`
}

// Business days after `from` up to and including `to` (0 when `to` is not
// after `from`). Delivered on a Monday, today the next Monday: 5.
export function businessDaysSince(from, to) {
  if (!isDate(from) || !isDate(to) || to <= from) return 0
  let n = 0
  const d = new Date(`${from}T00:00:00Z`)
  for (;;) {
    d.setUTCDate(d.getUTCDate() + 1)
    const iso = d.toISOString().slice(0, 10)
    if (iso > to) break
    if (isBusinessDay(iso)) n++
  }
  return n
}

// { overdue, businessDays, dueDate } for a delivered date, or null without a
// valid date. Overdue: delivered MORE than the window before today (5: the
// sixth business day after delivery is overdue). dueDate: delivered + the
// window, the "by" date of the Refund Delivered reply.
export function returnRefundOverdueState({ deliveredDate, today = overdueToday(), windowBusinessDays = RETURN_REFUND_OVERDUE_BUSINESS_DAYS } = {}) {
  if (!isDate(deliveredDate)) return null
  const window = Number.isInteger(Number(windowBusinessDays)) && Number(windowBusinessDays) >= 0 ? Number(windowBusinessDays) : RETURN_REFUND_OVERDUE_BUSINESS_DAYS
  const businessDays = businessDaysSince(deliveredDate, isDate(today) ? today : overdueToday())
  return { overdue: businessDays > window, businessDays, dueDate: addBusinessDays(deliveredDate, window), windowBusinessDays: window }
}

// The window a stage choice step carries, or null when it offers no overdue
// refund (no RMA, an older suggestion).
export function refundOverdueWindowOf(choiceStep) {
  const w = choiceStep?.params?.refundOverdue?.windowBusinessDays
  return choiceStep?.params?.kind === RETURN_STAGE_KIND && Number.isInteger(Number(w)) && w !== null ? Number(w) : null
}

// The path that is on for a choice: the choice itself, except "delivered"
// past the window on a stage step that offers the overdue refund, which is
// RETURN_STAGE_DELIVERED_OVERDUE. choice: { choice, date } (effectiveTaskPathChoice()).
export function returnStagePathKey(choiceStep, choice, today = overdueToday()) {
  const value = choice?.choice ?? null
  if (value !== 'delivered') return value
  const window = refundOverdueWindowOf(choiceStep)
  if (window == null) return value
  const state = returnRefundOverdueState({ deliveredDate: choice?.date ?? null, today, windowBusinessDays: window })
  return state?.overdue ? RETURN_STAGE_DELIVERED_OVERDUE : value
}

// The step card's line: why the refund step is there.
export function overdueRefundWhy({ deliveredDate, businessDays, windowBusinessDays = RETURN_REFUND_OVERDUE_BUSINESS_DAYS } = {}) {
  const on = mdyDate(deliveredDate)
  if (!on) return null
  return `Delivered to us on ${on}, ${businessDays} business day${businessDays === 1 ? '' : 's'} ago, and no refund yet (the warehouse usually refunds within ${windowBusinessDays}).`
}

// --- The refund's method ---------------------------------------------------------

export const OVERDUE_REFUND_METHODS = Object.freeze({
  ebay_order: 'eBay seller refund',
  ebay_return: 'eBay return refund',
  ebay_inquiry: 'eBay INR inquiry refund',
  website: 'Website refund',
})
export function overdueRefundMethodText(method) {
  return OVERDUE_REFUND_METHODS[method] ?? OVERDUE_REFUND_METHODS.ebay_order
}

// --- The email to Support ----------------------------------------------------------

// The values a body or note keeps as a bracketed token until they are known
// (fillOverdueRefundTokens()): the CSR's delivered date and the business days
// since, the CSR's name, the amount (the refund step's input), the refund's
// date, and the refund step's result (the RTN number, the eBay refund id).
// Never INSERT*: these are internal texts, filled in the panel as the values
// are entered and at Process.
export const OVERDUE_TOKENS = Object.freeze({
  amount: '[amount]',
  date: '[date]',
  deliveredDate: '[delivered date]',
  businessDays: '[business days]',
  csrName: '[CSR name]',
  rtnNumber: '[RTN number]',
  ebayRefundId: '[eBay refund id]',
})
const EBAY_REFUND_ID_CLAUSE_RE = /,\s*eBay refund id \[eBay refund id\]/g
// The marker CXHub sets on this email (2026-10-29): the subject prefix and a
// header, so the Support mailbox's ingest recognizes it as CXHub's own
// internal request (server/src/channels/m365/rma-zero-out.js).
export const RMA_ZERO_OUT_HEADER = 'X-CXHub-Request'
export const RMA_ZERO_OUT_HEADER_VALUE = 'rma-zero-out'
export const RMA_ZERO_OUT_SUBJECT_RE = /^\s*zero out rma\s+(\S+)\s+[–—-]\s+refunded by cxhub\s+[–—-]\s+(\S+)/i
export const RMA_ZERO_OUT_CONTACT_RE = /CXHub contact\s+#?(\d+)/i

const hasValue = (v) => v !== null && v !== undefined && v !== ''
const isNum = (v) => hasValue(v) && Number.isFinite(Number(v))
const amountText = (v) => (isNum(v) ? Number(v).toFixed(2) : OVERDUE_TOKENS.amount)
const daysText = (n) => (isNum(n) ? `${Number(n)} business day${Number(n) === 1 ? '' : 's'}` : `${OVERDUE_TOKENS.businessDays} business days`)

export function rmaZeroOutSubject({ rmaNumber, erpOrderId }) {
  return `Zero out RMA ${rmaNumber ?? ''} – refunded by CXHub – ${erpOrderId ?? ''}`.trim()
}

export function isEbayRefundMethod(method) {
  return method === 'ebay_order' || method === 'ebay_return' || method === 'ebay_inquiry'
}

// The email's body, plain text. A value not known yet stays as its token.
export function rmaZeroOutBody({ rmaNumber, erpOrderId, deliveredDate = null, businessDays = null, csrName = null, refundDate = null, rtnNumber = null, amount = null, method, ebayRefundId = null, contactNumber = null }) {
  const idPart = isEbayRefundMethod(method) ? `, eBay refund id ${ebayRefundId ?? OVERDUE_TOKENS.ebayRefundId}` : ''
  return [
    `Please zero out RMA ${rmaNumber} on order ${erpOrderId}.`,
    `The return was delivered to the FC on ${mdyDate(deliveredDate) ?? OVERDUE_TOKENS.deliveredDate} (${daysText(businessDays)} ago) and had not been refunded, so ${csrName || OVERDUE_TOKENS.csrName} issued the refund from CXHub on ${mdyDate(refundDate) ?? OVERDUE_TOKENS.date}: RTN ${rtnNumber ?? OVERDUE_TOKENS.rtnNumber}, $${amountText(amount)}, ${overdueRefundMethodText(method)}${idPart}.`,
    "Do not issue another refund for this RMA when it's received.",
    `CXHub contact ${contactNumber ?? ''}`.trim(),
  ].join('\n')
}

// The whole email: { to, from, subject, body }. to and from come from the
// server's mailbox config (the step's params), never from here.
export function rmaZeroOutEmail({ to, from, ...fields }) {
  return { to, from, subject: rmaZeroOutSubject(fields), body: rmaZeroOutBody(fields) }
}

// --- The order note ----------------------------------------------------------------

export function supportNotifiedSentence(rmaNumber) {
  return `Support was notified by email to zero out RMA ${rmaNumber}.`
}
export const SUPPORT_NOT_NOTIFIED_SENTENCE = 'Support has not been notified yet.'

export function overdueRefundNote({ amount = null, refundDate = null, rtnNumber = null, method, rmaNumber, deliveredDate = null, businessDays = null, supportNotified = false }) {
  const head = `Refund of $${amountText(amount)} issued by CXHub on ${mdyDate(refundDate) ?? OVERDUE_TOKENS.date} (RTN ${rtnNumber ?? OVERDUE_TOKENS.rtnNumber}, ${overdueRefundMethodText(method)}) for the items on RMA ${rmaNumber}: the return was delivered to the FC on ${mdyDate(deliveredDate) ?? OVERDUE_TOKENS.deliveredDate}, ${daysText(businessDays)} ago, with no refund.`
  return `${head} ${supportNotified ? supportNotifiedSentence(rmaNumber) : SUPPORT_NOT_NOTIFIED_SENTENCE}`
}

// The note's Support sentence as it stands at Process (Jenn: added only when
// the email was sent). Works on an edited note: the sentence is swapped when
// present, added when neither is.
export function withSupportNotified(note, rmaNumber, sent) {
  const yes = supportNotifiedSentence(rmaNumber)
  let text = String(note ?? '')
  const has = (s) => text.includes(s)
  if (sent) {
    if (has(SUPPORT_NOT_NOTIFIED_SENTENCE)) text = text.replace(SUPPORT_NOT_NOTIFIED_SENTENCE, yes)
    else if (!has(yes)) text = `${text.trim()} ${yes}`.trim()
  } else if (has(yes)) {
    text = text.replace(yes, SUPPORT_NOT_NOTIFIED_SENTENCE)
  } else if (!has(SUPPORT_NOT_NOTIFIED_SENTENCE)) {
    text = `${text.trim()} ${SUPPORT_NOT_NOTIFIED_SENTENCE}`.trim()
  }
  return text
}

// Fills every token whose value is given; the rest stay. final (at Process,
// after the refund ran): an RTN number the console did not return reads
// "(number not available yet)", and the eBay refund id clause is dropped when
// eBay returned none.
export function fillOverdueRefundTokens(text, { amount, refundDate, deliveredDate, businessDays, csrName, rtnNumber, ebayRefundId, final = false } = {}) {
  let out = String(text ?? '')
  const put = (token, value) => { out = out.split(token).join(value) }
  if (isNum(amount)) put(OVERDUE_TOKENS.amount, Number(amount).toFixed(2))
  if (mdyDate(refundDate)) put(OVERDUE_TOKENS.date, mdyDate(refundDate))
  if (mdyDate(deliveredDate)) put(OVERDUE_TOKENS.deliveredDate, mdyDate(deliveredDate))
  if (isNum(businessDays)) {
    put(`${OVERDUE_TOKENS.businessDays} business days`, daysText(businessDays))
    put(OVERDUE_TOKENS.businessDays, String(Number(businessDays)))
  }
  if (hasValue(csrName)) put(OVERDUE_TOKENS.csrName, String(csrName))
  if (hasValue(rtnNumber)) put(OVERDUE_TOKENS.rtnNumber, String(rtnNumber))
  else if (final) put(OVERDUE_TOKENS.rtnNumber, '(number not available yet)')
  if (hasValue(ebayRefundId)) put(OVERDUE_TOKENS.ebayRefundId, String(ebayRefundId))
  else if (final) out = out.replace(EBAY_REFUND_ID_CLAUSE_RE, '').split(OVERDUE_TOKENS.ebayRefundId).join('')
  return out
}

// The tokens still in a text (what Process refuses to send).
export function overdueTokensLeft(text) {
  const t = String(text ?? '')
  return Object.values(OVERDUE_TOKENS).filter((tok) => t.includes(tok))
}
